import { FunctionComponent } from "react";
import { SetPageTitle } from "../../components/SetPageTitle";
import { Badge, Box, Card, CardBody, Flex, Grid, Icon, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { ApplicationStatusWidget } from "./ApplicationStatusWidget";
import { PercentPie } from "./PercentPie";
import { useDashboard } from "../../api/hooks";
import { useParams } from "react-router-dom";
import { Shimmer } from "@am-tax/fe-core";
import { UsersWidget } from "./UsersWidget";
import { EntitiesWidget } from "./EntitiesWidget";
import { IconClipboardCheck, IconUsers, IconUserCheck, IconUserPlus } from "@tabler/icons-react";
import { WidgetHeading } from "./WidgetHeading";
import { useTenantUsers } from "../../api";

type StatCardProps = {
    title: string;
    value: number | string;
    sub?: string;
    trend?: string;
    accentColor: string;
    icon: typeof IconUsers;
    isLoading: boolean;
};

const StatCard: FunctionComponent<StatCardProps> = ({ title, value, sub, trend, accentColor, icon, isLoading }) => (
    <Shimmer isLoading={isLoading} height="110px">
        <Card variant="withShadow" overflow="hidden" h="110px">
            <Box h="3px" bg={accentColor} />
            <CardBody pt={3} pb={3}>
                <Flex justify="space-between" align="flex-start">
                    <VStack align="flex-start" spacing={1}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                            {title}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" lineHeight={1} color="gray.800">
                            {value}
                        </Text>
                        {(sub || trend) && (
                            <Flex gap={2} mt={1} align="center">
                                {sub && (
                                    <Text fontSize="xs" color="gray.500">
                                        {sub}
                                    </Text>
                                )}
                                {trend && (
                                    <Badge colorScheme="green" fontSize="xs" borderRadius="full" px={2}>
                                        {trend}
                                    </Badge>
                                )}
                            </Flex>
                        )}
                    </VStack>
                    <Icon as={icon} boxSize={6} color={accentColor} opacity={0.6} />
                </Flex>
            </CardBody>
        </Card>
    </Shimmer>
);

export const ClientDashboardPage: FunctionComponent = () => {
    const { cId } = useParams();
    const dashboardQuery = useDashboard(cId);
    const dashboardLoading = dashboardQuery.isLoading;

    const usersQuery = useTenantUsers(cId);
    const usersLoading = usersQuery.isLoading;

    const dashboard = dashboardQuery.data;
    const screens = dashboard?.screens;
    const submissions = dashboard?.submissions;
    const entities = dashboard?.entityDetails || [];
    const users = usersQuery.data;

    const totalCandidates = screens?.total || 0;
    const totalHired = screens?.hired || 0;
    const newCandidates = screens?.newCandidates || 0;
    const questionnairesCompleted = screens?.questionnairesCompleted || 0;

    return (
        <>
            <SetPageTitle title={"Dashboard"} />
            <Flex flexDirection="column" flexGrow={1} gap={"1.5rem"} width={"100%"}>
                {/* KPI Stat Cards */}
                <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={"1rem"}>
                    <StatCard
                        title="Total Candidates"
                        value={totalCandidates}
                        accentColor="var(--chakra-colors-blue-500)"
                        icon={IconUsers}
                        isLoading={dashboardLoading}
                    />
                    <StatCard
                        title="New Candidates"
                        value={newCandidates}
                        trend={newCandidates > 0 ? `+${newCandidates} this month` : undefined}
                        accentColor="var(--chakra-colors-green-500)"
                        icon={IconUserPlus}
                        isLoading={dashboardLoading}
                    />
                    <StatCard
                        title="Total Hired"
                        value={totalHired}
                        sub={`out of ${totalCandidates}`}
                        accentColor="var(--chakra-colors-teal-500)"
                        icon={IconUserCheck}
                        isLoading={dashboardLoading}
                    />
                    <StatCard
                        title="Questionnaires"
                        value={questionnairesCompleted}
                        sub={`out of ${totalCandidates}`}
                        accentColor="var(--chakra-colors-purple-500)"
                        icon={IconClipboardCheck}
                        isLoading={dashboardLoading}
                    />
                </SimpleGrid>

                {/* Success Rates + Application Status */}
                <Grid templateColumns={{ base: "1fr", xl: "2fr 3fr" }} gap={"1.5rem"}>
                    <Card variant={"withShadow"}>
                        <CardBody px={"1.25rem"} pt={"1rem"}>
                            <WidgetHeading title="Success Rates (YTD)" url="" />
                            <Flex wrap="wrap" justify="space-around" gap={4} pt={2}>
                                <PercentPie
                                    isLoading={dashboardLoading}
                                    color={"var(--chakra-colors-blue-600)"}
                                    target={screens?.screened}
                                    total={screens?.total}
                                    title="Screened"
                                    subtitle="[Screened] / [Total] * 100"
                                    description="Screened Candidates have statuses of Not Eligible, Eligible, Pending Certification, Certified, Denied, In Appeal, Appeal Denied, Claimed, Pending Documents or Unable To Substantiate."
                                />
                                <PercentPie
                                    isLoading={dashboardLoading}
                                    color={"var(--chakra-colors-cyan-500)"}
                                    target={screens?.eligible}
                                    total={screens?.total}
                                    title="Eligible"
                                    subtitle="[Eligible] / [Total] * 100"
                                    description={
                                        <>
                                            Eligible Candidates have statuses that are <u>NOT</u> "Not Screened, Screening, Opted Out, Not Eligible, Unable To
                                            Substantiate, Denied, Appeal Denied".
                                        </>
                                    }
                                />
                                <PercentPie
                                    isLoading={dashboardLoading}
                                    color={"var(--chakra-colors-green-500)"}
                                    target={screens?.hired}
                                    total={screens?.total}
                                    title="Eligible Hired"
                                    subtitle="[Hired] / [Total] * 100"
                                    description={
                                        <>
                                            Eligible Hired Candidates have statuses that are <u>NOT</u> "Not Screened, Screening, Opted Out, Not Eligible,
                                            Unable To Substantiate, Denied, Appeal Denied, Not Hired".
                                        </>
                                    }
                                />
                            </Flex>
                        </CardBody>
                    </Card>
                    <ApplicationStatusWidget isLoading={dashboardLoading} submissions={submissions} />
                </Grid>

                {/* Entities Table */}
                <EntitiesWidget isLoading={dashboardLoading} entities={entities} />

                {/* Users */}
                <UsersWidget isLoading={usersLoading} users={users} clientId={cId} />
            </Flex>
        </>
    );
};
